import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiUpload, FiX, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { createProduct, updateProduct, getProduct, getCategories } from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import './AddProduct.css';

const INITIAL = {
  name: '', description: '', price: '', salePrice: '', stock: '', sku: '',
  category: '', tags: '', weight: '', length: '', width: '', height: '',
};

export default function AddProduct() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL);
  const [images, setImages] = useState([]);       // { file, preview } or { url } (existing)
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getCategories().then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    if (isEdit) {
      getProduct(id)
        .then(({ data }) => {
          const p = data.product;
          setForm({
            name: p.name || '',
            description: p.description || '',
            price: p.price ?? '',
            salePrice: p.salePrice ?? '',
            stock: p.stock ?? '',
            sku: p.sku || '',
            category: p.category?._id || p.category || '',
            tags: (p.tags || []).join(', '),
            weight: p.shipping?.weight ?? '',
            length: p.shipping?.dimensions?.length ?? '',
            width: p.shipping?.dimensions?.width ?? '',
            height: p.shipping?.dimensions?.height ?? '',
          });
          setImages((p.images || []).map((url) => ({ url })));
        })
        .catch(() => toast.error('Failed to load product'));
    }
  }, [id, isEdit]);

  const addFiles = useCallback((files) => {
    const valid = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (images.length + valid.length > 6) {
      toast.error('Maximum 6 images allowed');
      return;
    }
    const newImgs = valid.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setImages((prev) => [...prev, ...newImgs]);
  }, [images.length]);

  const removeImage = (idx) => {
    setImages((prev) => {
      const copy = [...prev];
      if (copy[idx].preview) URL.revokeObjectURL(copy[idx].preview);
      copy.splice(idx, 1);
      return copy;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Product name is required');
    if (!form.price || Number(form.price) <= 0) return toast.error('Valid price is required');
    if (!isEdit && images.length === 0) return toast.error('At least one image is required');

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('description', form.description.trim());
      fd.append('price', form.price);
      if (form.salePrice) fd.append('salePrice', form.salePrice);
      fd.append('stock', form.stock || 0);
      if (form.sku) fd.append('sku', form.sku.trim());
      if (form.category) fd.append('category', form.category);
      if (form.tags) {
        const tagArr = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
        tagArr.forEach((t) => fd.append('tags[]', t));
      }
      if (form.weight) fd.append('shipping[weight]', form.weight);
      if (form.length) fd.append('shipping[dimensions][length]', form.length);
      if (form.width) fd.append('shipping[dimensions][width]', form.width);
      if (form.height) fd.append('shipping[dimensions][height]', form.height);

      images.filter((img) => img.file).forEach((img) => fd.append('images', img.file));
      const existingUrls = images.filter((img) => img.url).map((img) => img.url);
      existingUrls.forEach((url) => fd.append('existingImages[]', url));

      if (isEdit) {
        await updateProduct(id, fd);
        toast.success('Product updated!');
      } else {
        await createProduct(fd);
        toast.success('Product created!');
      }
      navigate('/vendor/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <VendorLayout title={isEdit ? 'Edit Product' : 'Add Product'}>
      <div className="ap-page">
        <form className="ap-form" onSubmit={handleSubmit} noValidate>
          {/* Images */}
          <section className="ap-section card">
            <h2 className="ap-section-title">Product Images</h2>
            <p className="ap-hint">Up to 6 images. First image is the main photo.</p>

            <div
              className={`ap-dropzone ${dragging ? 'dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUpload size={28} className="ap-drop-icon" />
              <p className="ap-drop-text">Drag &amp; drop images here, or <span className="ap-drop-link">browse</span></p>
              <p className="ap-drop-sub">JPEG, PNG, WEBP up to 5MB each</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => addFiles(e.target.files)}
              />
            </div>

            {images.length > 0 && (
              <div className="ap-image-grid">
                {images.map((img, idx) => (
                  <div key={idx} className="ap-image-item">
                    <img src={img.preview || img.url} alt="" className="ap-image-preview" />
                    {idx === 0 && <span className="ap-image-main">Main</span>}
                    <button
                      type="button"
                      className="ap-image-remove"
                      onClick={() => removeImage(idx)}
                      aria-label="Remove image"
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ))}
                {images.length < 6 && (
                  <button
                    type="button"
                    className="ap-image-add"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FiPlus size={20} />
                    <span>Add</span>
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Basic info */}
          <section className="ap-section card">
            <h2 className="ap-section-title">Basic Information</h2>
            <div className="ap-grid-1">
              <div className="form-group">
                <label className="form-label">Product Name *</label>
                <input
                  name="name"
                  className="form-input"
                  placeholder="e.g. Wireless Noise-Cancelling Headphones"
                  value={form.name}
                  onChange={handleChange}
                  maxLength={200}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-textarea"
                  placeholder="Describe your product in detail…"
                  value={form.description}
                  onChange={handleChange}
                  rows={5}
                />
              </div>
            </div>

            <div className="ap-grid-3">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select name="category" className="form-select" value={form.category} onChange={handleChange}>
                  <option value="">— Select category —</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">SKU</label>
                <input
                  name="sku"
                  className="form-input"
                  placeholder="e.g. WH-NC-001"
                  value={form.sku}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Tags <span className="ap-hint-inline">(comma-separated)</span></label>
                <input
                  name="tags"
                  className="form-input"
                  placeholder="wireless, audio, gaming"
                  value={form.tags}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          {/* Pricing & Stock */}
          <section className="ap-section card">
            <h2 className="ap-section-title">Pricing &amp; Stock</h2>
            <div className="ap-grid-3">
              <div className="form-group">
                <label className="form-label">Price (Rs. ) *</label>
                <input
                  type="number"
                  name="price"
                  className="form-input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Sale Price (Rs. ) <span className="ap-hint-inline">optional</span></label>
                <input
                  type="number"
                  name="salePrice"
                  className="form-input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  value={form.salePrice}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Stock Quantity</label>
                <input
                  type="number"
                  name="stock"
                  className="form-input"
                  placeholder="0"
                  min="0"
                  value={form.stock}
                  onChange={handleChange}
                />
              </div>
            </div>
            {form.salePrice && Number(form.salePrice) >= Number(form.price) && (
              <p className="ap-warn">Sale price should be less than the regular price.</p>
            )}
          </section>

          {/* Shipping */}
          <section className="ap-section card">
            <h2 className="ap-section-title">Shipping <span className="ap-hint-inline">(optional)</span></h2>
            <div className="ap-grid-4">
              <div className="form-group">
                <label className="form-label">Weight (kg)</label>
                <input type="number" name="weight" className="form-input" placeholder="0.0" min="0" step="0.01" value={form.weight} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Length (cm)</label>
                <input type="number" name="length" className="form-input" placeholder="0" min="0" value={form.length} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Width (cm)</label>
                <input type="number" name="width" className="form-input" placeholder="0" min="0" value={form.width} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Height (cm)</label>
                <input type="number" name="height" className="form-input" placeholder="0" min="0" value={form.height} onChange={handleChange} />
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="ap-footer">
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/vendor/products')} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update Product' : 'Publish Product'}
            </button>
          </div>
        </form>
      </div>
    </VendorLayout>
  );
}
